// Allowlist HTML sanitizer for rendering email bodies and signatures. Parses the
// HTML in the browser and rebuilds it, keeping only safe tags/attributes and
// dropping scripts, inline event handlers, and dangerous style/URL values.
// Disallowed tags are unwrapped (their text/children are kept).
//
// Table markup is supported because email signatures are almost always built
// with tables; without it their layout collapses and wraps incorrectly.

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
  'A', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'SPAN', 'DIV', 'IMG', 'FONT',
  'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'CAPTION', 'COLGROUP', 'COL',
  'HR', 'SMALL', 'SUB', 'SUP',
])

// Attributes allowed on every permitted tag.
const GLOBAL_ATTRS = new Set(['style', 'align', 'title', 'dir'])

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(['href', 'target', 'rel']),
  IMG: new Set(['src', 'alt', 'width', 'height']),
  FONT: new Set(['color', 'face', 'size']),
  TABLE: new Set(['border', 'cellpadding', 'cellspacing', 'width', 'height', 'valign', 'bgcolor']),
  TD: new Set(['colspan', 'rowspan', 'width', 'height', 'valign', 'nowrap', 'bgcolor']),
  TH: new Set(['colspan', 'rowspan', 'width', 'height', 'valign', 'nowrap', 'bgcolor']),
  TR: new Set(['valign', 'height', 'bgcolor']),
  COL: new Set(['span', 'width']),
  COLGROUP: new Set(['span', 'width']),
}

const EMPTY = new Set<string>()

// Remove dangerous CSS declarations (scripts, external/behavioral loads, and
// layout escapes) while keeping presentational styles signatures rely on.
function sanitizeStyle(value: string): string {
  return value
    .split(';')
    .map(d => d.trim())
    .filter(d => {
      if (!d) return false
      if (/(url\s*\(|expression\s*\(|javascript:|@import|behavior\s*:|position\s*:)/i.test(d)) return false
      return true
    })
    .join('; ')
}

function sanitizeNode(node: Node, out: Node, doc: Document): void {
  node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(doc.createTextNode(child.textContent ?? ''))
      return
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return

    const el = child as Element
    if (ALLOWED_TAGS.has(el.tagName)) {
      const clean = doc.createElement(el.tagName.toLowerCase())
      const allowed = ALLOWED_ATTRS[el.tagName] ?? EMPTY
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase()
        if (name.startsWith('on')) continue
        if (!GLOBAL_ATTRS.has(name) && !allowed.has(name)) continue
        if (name === 'style') {
          const safe = sanitizeStyle(attr.value)
          if (safe) clean.setAttribute('style', safe)
          continue
        }
        clean.setAttribute(attr.name, attr.value)
      }
      if (el.tagName === 'A') {
        const href = clean.getAttribute('href') ?? ''
        if (!/^(https?:|mailto:)/i.test(href.trim())) {
          clean.removeAttribute('href')
        } else {
          clean.setAttribute('target', '_blank')
          clean.setAttribute('rel', 'noopener noreferrer')
        }
      }
      if (el.tagName === 'IMG') {
        const src = clean.getAttribute('src') ?? ''
        if (!/^(https?:|data:image\/)/i.test(src.trim())) {
          return
        }
      }
      sanitizeNode(el, clean, doc)
      out.appendChild(clean)
    } else {
      // Unwrap disallowed elements: keep their (sanitized) children.
      sanitizeNode(el, out, doc)
    }
  })
}

export function sanitizeHtml(html: string): string {
  if (typeof window === 'undefined' || !html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const container = doc.createElement('div')
  sanitizeNode(doc.body, container, doc)
  return container.innerHTML
}

/** True when the string appears to contain HTML markup (an element tag). */
export function looksLikeHtml(value: string): boolean {
  return /<([a-z][a-z0-9]*)\b[^>]*>/i.test(value)
}
